import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useFormContext } from "react-hook-form";
import { FullOnboardingForm } from "lib/schema";
import {
  useAddBankAccountMutation,
  useSkipBankSetupMutation,
  AddBankAccountCA,
  AddBankAccountUS,
} from "components/Slices/usersApiSlice";
import { CANADIAN_BANKS } from "lib/bankLogos";
import { useNavigate } from "react-router-dom";

interface PaymentSetupStepProps {
  onBack: () => void;
  bankSkipped?: boolean;
  userStatus?: string;
}

export const PaymentSetupStep = ({ onBack, bankSkipped, userStatus }: PaymentSetupStepProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { watch } = useFormContext<FullOnboardingForm>();
  const [addBankAccount, { isLoading }] = useAddBankAccountMutation();
  const [skipBankSetup, { isLoading: isSkipping }] = useSkipBankSetupMutation();

  const isVerified = userStatus === "active";
  const country = watch("country") === "US" ? "US" : "CA";

  const [accountHolderName, setAccountHolderName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [selectedBank, setSelectedBank] = useState("");
  const [institutionNumber, setInstitutionNumber] = useState("");
  const [isCustomInstitution, setIsCustomInstitution] = useState(false);
  const [transitNumber, setTransitNumber] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleBankChange = (value: string) => {
    if (value === "custom") {
      setIsCustomInstitution(true);
      setSelectedBank("custom");
      setInstitutionNumber("");
    } else {
      setIsCustomInstitution(false);
      setSelectedBank(value);
      const bank = CANADIAN_BANKS.find((b) => b.institutionNumber === value);
      if (bank) setInstitutionNumber(bank.institutionNumber);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (country === "CA") {
        const data: AddBankAccountCA = { country: "CA", accountHolderName, institutionNumber, transitNumber, accountNumber };
        await addBankAccount(data).unwrap();
      } else {
        const data: AddBankAccountUS = { country: "US", accountHolderName, routingNumber, accountNumber };
        await addBankAccount(data).unwrap();
      }
      setSuccess(true);
      setTimeout(() => navigate("/onboarding/success"), 1500);
    } catch (err: any) {
      setError(err?.data?.error || err?.data?.message || "Failed to add bank account. Please try again.");
    }
  };

  const handleSkip = async () => {
    try {
      await skipBankSetup().unwrap();
    } catch {
      // non-critical
    }
    navigate("/onboarding/success");
  };

  if (success) {
    return (
      <Stack spacing={2} alignItems="center" py={4}>
        <CheckCircleIcon sx={{ fontSize: 48, color: "success.main" }} />
        <Typography variant="h6" fontWeight={600} color="success.main">
          Bank account added
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Redirecting to your dashboard...
        </Typography>
        <CircularProgress size={20} color="success" />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Link your bank account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Required to receive payouts from your circles. You can also add this later in Settings.
        </Typography>
      </Box>

      {!isVerified && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Identity verification must be completed before adding a payout account. You can continue to the home page and add one later in Settings.
        </Alert>
      )}

      {bankSkipped && isVerified && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          You previously skipped this step. You can add a bank account now or go to your dashboard — you can always add one later in Settings.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Box
        component="form"
        id="bank-form"
        onSubmit={handleSubmit}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
          opacity: isVerified ? 1 : 0.5,
          pointerEvents: isVerified ? "auto" : "none",
        }}
      >
        <Stack divider={<Divider />}>
          {/* Country — read only */}
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Country
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {country === "CA" ? "Canada" : "United States"}
            </Typography>
          </Box>

          {/* Account holder */}
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <TextField
              label="Account holder name"
              placeholder="Jane Smith"
              value={accountHolderName}
              onChange={(e) => setAccountHolderName(e.target.value)}
              required
              fullWidth
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{ "& .MuiInputBase-root": { fontSize: "0.95rem" } }}
            />
          </Box>

          {/* Canada-specific */}
          {country === "CA" && (
            <>
              <Box sx={{ px: 2.5, py: 1.5 }}>
                <FormControl fullWidth required variant="standard">
                  <InputLabel>Bank</InputLabel>
                  <Select
                    value={selectedBank}
                    onChange={(e) => handleBankChange(e.target.value as string)}
                    label="Bank"
                    disableUnderline
                    sx={{ fontSize: "0.95rem" }}
                  >
                    {CANADIAN_BANKS.map((bank) => (
                      <MenuItem key={bank.institutionNumber} value={bank.institutionNumber}>
                        {bank.name}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem value="custom">Other — enter manually</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {isCustomInstitution && (
                <Box sx={{ px: 2.5, py: 1.5 }}>
                  <TextField
                    label="Institution number"
                    placeholder="001"
                    value={institutionNumber}
                    onChange={(e) => setInstitutionNumber(e.target.value)}
                    required
                    fullWidth
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    inputProps={{ maxLength: 3 }}
                    helperText="3-digit number from your cheque"
                    sx={{ "& .MuiInputBase-root": { fontSize: "0.95rem" } }}
                  />
                </Box>
              )}

              <Box sx={{ px: 2.5, py: 1.5 }}>
                <TextField
                  label="Transit number"
                  placeholder="12345"
                  value={transitNumber}
                  onChange={(e) => setTransitNumber(e.target.value)}
                  required
                  fullWidth
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ maxLength: 5 }}
                  helperText="5-digit branch number"
                  sx={{ "& .MuiInputBase-root": { fontSize: "0.95rem" } }}
                />
              </Box>
            </>
          )}

          {/* US-specific */}
          {country === "US" && (
            <Box sx={{ px: 2.5, py: 1.5 }}>
              <TextField
                label="Routing number"
                placeholder="110000000"
                value={routingNumber}
                onChange={(e) => setRoutingNumber(e.target.value)}
                required
                fullWidth
                variant="standard"
                InputProps={{ disableUnderline: true }}
                inputProps={{ maxLength: 9 }}
                sx={{ "& .MuiInputBase-root": { fontSize: "0.95rem" } }}
              />
            </Box>
          )}

          {/* Account number */}
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <TextField
              label="Account number"
              placeholder={country === "CA" ? "1234567" : "000123456789"}
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              required
              fullWidth
              variant="standard"
              InputProps={{ disableUnderline: true }}
              inputProps={{ maxLength: country === "CA" ? 12 : 17 }}
              sx={{ "& .MuiInputBase-root": { fontSize: "0.95rem" } }}
            />
          </Box>
        </Stack>
      </Box>

      {/* Security note */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
        }}
      >
        <LockIcon sx={{ fontSize: 14, color: "text.disabled" }} />
        <Typography variant="caption" color="text.disabled">
          Encrypted and transmitted securely via Stripe
        </Typography>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onBack}
          disabled={isLoading}
          startIcon={<ArrowBackIcon />}
          sx={{ py: 1.5, fontWeight: 600, flex: 1 }}
        >
          Back
        </Button>
        <Button
          type="submit"
          form="bank-form"
          variant="contained"
          size="large"
          disabled={isLoading || !isVerified}
          endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
          sx={{
            py: 1.5,
            fontWeight: 600,
            flex: 2,
            background: isVerified
              ? `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
              : undefined,
          }}
        >
          {isLoading ? "Adding..." : "Add bank account"}
        </Button>
      </Box>

      {/* Skip / Continue */}
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant={isVerified ? "text" : "contained"}
          size={isVerified ? "small" : "large"}
          onClick={handleSkip}
          disabled={isLoading || isSkipping}
          sx={isVerified
            ? { color: "text.secondary", fontSize: "0.8rem" }
            : { py: 1.5, fontWeight: 600 }
          }
        >
          {isSkipping
            ? <CircularProgress size={14} color="inherit" />
            : isVerified
              ? "Skip for now — add later in Settings"
              : "Continue to home page"}
        </Button>
      </Box>
    </Stack>
  );
};
